import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import plannerPreview from "@/assets/planner-preview-month.jpg?w=800&format=webp;jpg&as=picture";
import kitPreview from "@/assets/caregiver-team.jpg?w=800&format=webp;jpg&as=picture";
import bookCover from "@/assets/survival-guide-cover.webp.asset.json";

type PictureAsset = { sources: Record<string, string>; img: { src: string; w: number; h: number } };

type Product = {
  to: string;
  picture?: PictureAsset;
  src?: string;
  alt: string;
  eyebrow: string;
  title: string;
  blurb: string;
  price: string;
  cta: string;
};

const products: Product[] = [
  {
    to: "/shop/book",
    src: bookCover.url,
    alt: "The Long-Distance Daughter's (and Son's) Survival Guide book cover",
    eyebrow: "Instant download",
    title: "The Long-Distance Survival Guide",
    blurb:
      "Caring for a parent from another city: building a remote care team, splitting the load with siblings, and the scripts for the hardest conversations. Three fillable templates included.",
    price: "$10.99",
    cta: "See the book",
  },
  {
    to: "/shop/caregiver-kit",
    picture: kitPreview as unknown as PictureAsset,
    alt: "The Complete Caregiver Companion — a fillable caregiver planning kit",
    eyebrow: "Instant download",
    title: "The Complete Caregiver Companion",
    blurb:
      "Eight fillable modules for medications, routines, contacts and appointments — everything a family needs to organize care in one place.",
    price: "From $30",
    cta: "See the kit",
  },
  {
    to: "/shop/wellness-planner",
    picture: plannerPreview as unknown as PictureAsset,
    alt: "Monthly overview page from the Caregiver Wellness Planner",
    eyebrow: "12-month planner",
    title: "The Caregiver Wellness Planner",
    blurb:
      "August 2026 through July 2027. Monthly overviews, weekly spreads and a daily wellness page — fillable on any device or printable.",
    price: "From $8",
    cta: "See the planner",
  },
];

const Picture = ({ picture, alt }: { picture: PictureAsset; alt: string }) => (
  <picture>
    {Object.entries(picture.sources).map(([type, srcSet]) => (
      <source key={type} type={`image/${type}`} srcSet={srcSet} />
    ))}
    <img
      src={picture.img.src}
      alt={alt}
      width={picture.img.w}
      height={picture.img.h}
      loading="lazy"
      className="w-full h-52 object-cover"
    />
  </picture>
);

interface Props {
  heading?: string;
  eyebrow?: string;
  intro?: string;
  className?: string;
}

const DigitalProductsPromo = ({
  heading = "Guides & Planners You Can Download Today",
  eyebrow = "The Kinship Shop",
  intro = "Not ready for in-home care — or caring for someone yourself? Our fillable digital guides bring the same Kinship standard to your own kitchen table.",
  className = "py-20 md:py-28 bg-card",
}: Props) => (
  <section className={className} aria-labelledby="digital-shop-heading">
    <div className="container">
      <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-warm-ink text-center mb-4 font-medium">{eyebrow}</p>
      <h2 id="digital-shop-heading" className="text-3xl md:text-4xl font-heading font-semibold text-center mb-5">
        {heading}
      </h2>
      <div className="w-16 h-px bg-warm mx-auto mb-6" />
      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12 leading-relaxed font-light">{intro}</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {products.map((p) => (
          <article key={p.to} className="bg-background border border-border rounded-2xl overflow-hidden flex flex-col">
            <Link to={p.to} aria-label={p.title}>
              {p.picture ? (
                <Picture picture={p.picture} alt={p.alt} />
              ) : (
                <img
                  src={p.src}
                  alt={p.alt}
                  width={1024}
                  height={1536}
                  loading="lazy"
                  className="w-full h-52 object-contain bg-secondary/40 py-3"
                />
              )}
            </Link>
            <div className="p-6 flex flex-col flex-1">
              <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-warm-ink font-medium mb-3">
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {p.eyebrow}
              </p>
              <h3 className="text-xl font-heading font-semibold mb-2">
                <Link to={p.to} className="hover:underline">
                  {p.title}
                </Link>
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-5 flex-1">{p.blurb}</p>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium">{p.price}</span>
                <Button asChild variant="outline">
                  <Link to={p.to}>
                    {p.cta} <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default DigitalProductsPromo;
